/**
 * All-watchlists target rows for one position (PV10, SE-8337): the distinct
 * targets across the user's accessible lists whose natural position maps
 * onto the slot, paged until complete and projected as read-only rows.
 *
 * The distinct-targets read is relay-paginated in the cache (keyed on its
 * filters, not on `first`), so a later page merges by itself and a page
 * completing after the filter changed lands in its own entry.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { PositionPopoutRow } from "@/features/scouting/components/molecules/WatchlistPositionPopoutRow";
import {
  dedupeTargetsByPlayerId,
  filterTargetsWithPlayer,
} from "@/features/scouting/hooks/useAllScoutingTargets";
import { useScoutingListAllTargetsQuery } from "@/generated/graphql";

/** Page size the All Targets table already uses. */
const PAGE_SIZE = 50;

/** Inputs for the rows. */
export interface UsePositionTargetRowsOptions {
  readonly isOpen: boolean;
  /** Natural-position ids of the slot's group. */
  readonly positionIds: readonly string[];
  readonly search: string;
}

/** Rows and paging state. */
export interface PositionTargetRows {
  readonly rows: readonly PositionPopoutRow[];
  /** Distinct total before the search, from the read. */
  readonly totalCount: number;
  readonly isComplete: boolean;
  /** False once the read failed: an error is not a load in progress. */
  readonly loading: boolean;
  /** First-read or later-page failure, until a retry lands. */
  readonly error?: Error;
  /** Re-reads after a first-read failure, or re-requests the failed page. */
  readonly retry: () => void;
}

/**
 * Reads and pages one position's distinct targets across the user's lists.
 * @param options - Open state, position ids and search.
 * @param options.isOpen - Whether the pop-out is open on this pool.
 * @param options.positionIds - Natural-position ids of the slot's group.
 * @param options.search - Search text, trimmed by the hook.
 * @returns Read-only rows and paging state.
 */
const usePositionTargetRows = ({
  isOpen,
  positionIds,
  search,
}: UsePositionTargetRowsOptions): PositionTargetRows => {
  const trimmed = search.trim();
  const skip = !isOpen || positionIds.length === 0;
  const { data, fetchMore, loading, error, refetch } =
    useScoutingListAllTargetsQuery({
      variables: {
        first: PAGE_SIZE,
        filterByPositionIds: [...positionIds],
        ...(trimmed ? { search: trimmed } : {}),
      },
      skip,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
    });
  const connection = data?.listAllScoutingTargets;
  const pageInfo = connection?.pageInfo;
  const queryKey = `${positionIds.join(",")}|${trimmed}`;
  const requested = useRef<string | null>(null);
  const [pageError, setPageError] = useState<Error | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  // A new query starts on its first page: its predecessor's page failure is
  // not its own (adjust-during-render, no effect).
  const [errorQueryKey, setErrorQueryKey] = useState(queryKey);
  if (errorQueryKey !== queryKey) {
    setErrorQueryKey(queryKey);
    setPageError(null);
  }
  const rows = useMemo<readonly PositionPopoutRow[]>(
    () =>
      skip
        ? []
        : dedupeTargetsByPlayerId(
            filterTargetsWithPlayer(connection?.edges ?? [])
          ).map((edge, index) => ({
            playerId: edge.node.player.id,
            displayName: edge.node.player.name,
            rank: index + 1,
            gpr: edge.node.player.bioData?.gpr ?? null,
          })),
    [connection?.edges, skip]
  );
  const isComplete = connection ? !connection.pageInfo.hasNextPage : false;
  const failure = error ?? pageError ?? undefined;
  const retry = useCallback(() => {
    if (error) {
      void refetch().catch(() => undefined);
      return;
    }
    requested.current = null;
    setPageError(null);
    setRetryTick(tick => tick + 1);
  }, [error, refetch]);

  useLayoutEffect(() => {
    requested.current = null;
  }, [queryKey]);
  useEffect(() => {
    const after = pageInfo?.endCursor;
    if (skip || pageError || !pageInfo?.hasNextPage || !after) return;
    const token = `${queryKey}|${after}`;
    if (requested.current === token) return;
    requested.current = token;
    void fetchMore({ variables: { after } }).catch((reason: unknown) => {
      if (requested.current !== token) return;
      requested.current = null;
      setPageError(
        reason instanceof Error ? reason : new Error(String(reason))
      );
    });
  }, [fetchMore, pageError, pageInfo, queryKey, retryTick, skip]);

  return {
    rows,
    totalCount: connection?.totalCount ?? 0,
    isComplete,
    loading: !skip && !failure && (loading || !isComplete),
    error: failure,
    retry,
  };
};

export default usePositionTargetRows;
